import { SwitchItem } from 'ui-components/dropdown-with-switches/dropdown-with-switches';
import { MultiSelectOption } from 'ui-components/multi-select/types';
import {
    useGetAvailableCategories,
    useGetAvailableRegionSubCategories,
    useGetAvailableSubCategories,
} from 'store/configuration/categories/selectors/selectors-hooks';
import { useCallback, useMemo } from 'react';
import { BILLBOARD, COMPLEX, MARKET, PROPERTY, REGION } from 'core/constants/report-type';
import { FILTER_TYPE_LABELS, hiddenCategories, RestrictedItems } from 'features/explore/constants';
import { cloneDeep } from 'lodash';
import { getCheckedItems } from 'ui-components/dropdown-with-switches/utils/utils';
import { useFiltersConfiguration } from 'features/explore/hooks/use-explore-persistent-filter';
import { getExploreFilterType } from 'utils/get-explore-filter-type/get-explore-filter-type';
import { Options } from 'ui-components/select-menu/select-menu';
import { ExploreLegendItem } from 'features/explore/types/types';
import { RestrictionsType } from 'types/entity-search';
import { MapLayerPOIType } from 'ui-components/google-map-layers/utils/map-layers-entity-color';
import type { ConfigurationCategoriesData } from '@placer-ui/types';
import {
    ALL_CATEGORY_SECTIONS,
    MARKET_CATEGORY_SECTION,
} from 'store/configuration/categories/consts';
import { getFeatureFlags, getPermissions } from 'core/flow-control';
import { MARKET_REGION_MULTISELECT_TREE, SectionValues } from 'features/explore/shared/constants';
import { useExploreMarketType } from 'features/explore/hooks/use-explore-market-type';

export const getExploreTypesOptions = (): Options[] => {
    const { show_billboards, show_qa_features } = getPermissions();
    const { enable_explore_markets_ff } = getFeatureFlags();

    const typesOptions: Options[] = [
        {
            value: getExploreFilterType(PROPERTY),
            label: FILTER_TYPE_LABELS[PROPERTY],
        },
    ];

    typesOptions.push({
        value: getExploreFilterType(REGION),
        label: FILTER_TYPE_LABELS[enable_explore_markets_ff ? MARKET : REGION],
    });

    if (show_billboards) {
        typesOptions.push({
            value: getExploreFilterType(BILLBOARD),
            label: FILTER_TYPE_LABELS[BILLBOARD],
        });
    }

    if (show_qa_features) {
        typesOptions.push({
            value: getExploreFilterType(COMPLEX),
            label: FILTER_TYPE_LABELS[COMPLEX],
        });
    }

    return typesOptions;
};

const getFilterOption = (
    id: RestrictionsType,
    title: string,
    checked = true,
    profile: MapLayerPOIType,
) => {
    return {
        id,
        title,
        checked,
        label: title,
        profile,
    };
};

export const usePoiFiltersOptions = () => {
    const { restrictions } = useFiltersConfiguration();
    const isMarket = useExploreMarketType();

    return useMemo(() => {
        const filterOptions: (SwitchItem<RestrictionsType> & ExploreLegendItem)[] = [
            getFilterOption(
                'enable_available_pois',
                RestrictedItems.ENABLE_AVAILABLE_POIS,
                restrictions['enable_available_pois'],
                'available',
            ),
        ];

        filterOptions.push(
            getFilterOption(
                'enable_nearby_activity_pois',
                RestrictedItems.ENABLE_NEARBY_ACTIVITY_POIS,
                restrictions['enable_nearby_activity_pois'],
                'nearby_activity',
            ),
        );

        filterOptions.push(
            getFilterOption(
                'enable_custom_pois',
                RestrictedItems.ENABLE_CUSTOM_POIS,
                restrictions['enable_custom_pois'],
                'custom',
            ),
        );

        if (isMarket) {
            filterOptions.push(
                getFilterOption(
                    'include_verified',
                    RestrictedItems.ENABLE_PUBLIC_MARKETS,
                    restrictions['include_verified'],
                    'available',
                ),
            );

            filterOptions.push(
                getFilterOption(
                    'include_unverified',
                    RestrictedItems.ENABLE_PRIVATE_MARKETS,
                    restrictions['include_unverified'],
                    'custom',
                ),
            );
        }

        filterOptions.push(
            getFilterOption(
                'enable_closed_pois',
                RestrictedItems.ENABLE_CLOSED_POIS,
                restrictions['enable_closed_pois'],
                'closed',
            ),
        );

        const checkedItems = getCheckedItems(cloneDeep(filterOptions));

        return filterOptions.map((item) => {
            return {
                ...item,
                disabled: checkedItems.includes(item.id) && checkedItems.length === 1,
            };
        });
    }, [isMarket, restrictions]);
};

export const useGetOptionValue = () => {
    const marketSubCategories = useGetAvailableSubCategories(MARKET_CATEGORY_SECTION);
    const regionSubCategories = useGetAvailableRegionSubCategories();

    return useCallback(
        (category: SectionValues): string | undefined => {
            const includeMarket =
                category?.marketValue && marketSubCategories.includes(category.marketValue);
            const includeRegion =
                category?.regionValue && regionSubCategories.includes(category.regionValue);

            if (!includeMarket && !includeRegion) {
                return undefined;
            }
            return [
                includeMarket ? category.marketValue : '',
                includeRegion ? category.regionValue : '',
            ]
                .filter(Boolean)
                .join(',');
        },
        [marketSubCategories, regionSubCategories],
    );
};

export const useMarketRegionCategories = (selectedCategories: string[]) => {
    const getOptionValue = useGetOptionValue();

    return useMemo(() => {
        return MARKET_REGION_MULTISELECT_TREE.reduce<MultiSelectOption[]>(
            (multiSelectAcc, primary) => {
                const subOptions = primary.subOptions?.reduce<MultiSelectOption[]>(
                    (subOptionsAcc, subCategory) => {
                        const value = getOptionValue(subCategory);
                        if (!value) {
                            return subOptionsAcc;
                        }

                        subOptionsAcc.push({
                            name: subCategory.name!,
                            value: value,
                            selected:
                                selectedCategories?.includes(value) || !selectedCategories?.length,
                            key: `${primary.name}-${subCategory.name}`,
                        });

                        return subOptionsAcc;
                    },
                    [],
                );

                if (subOptions?.length) {
                    multiSelectAcc.push({
                        value: primary.value,
                        name: primary.name,
                        selected: subOptions?.every(({ selected }) => selected) ?? true,
                        key: primary.name,
                        subOptions,
                    });
                }

                if (primary.sectionValues) {
                    const value = getOptionValue(primary.sectionValues);
                    value &&
                        multiSelectAcc.push({
                            value: value,
                            name: primary.name,
                            selected:
                                selectedCategories?.includes(value) || !selectedCategories?.length,
                            key: primary.name,
                        });
                }

                return multiSelectAcc;
            },
            [],
        );
    }, [getOptionValue, selectedCategories]);
};

export const useRegionCategories = () => {
    const categories = useGetAvailableCategories(ALL_CATEGORY_SECTIONS);

    const { type } = useFiltersConfiguration();

    return useMemo(() => {
        if (type !== REGION) return categories;

        return categories.reduce<ConfigurationCategoriesData[]>((acc, group) => {
            if (group.label === 'Region') {
                acc.push({
                    ...group,
                    children: group.children!.filter(
                        (primary) => !hiddenCategories.includes(primary.label),
                    ),
                });
            }
            return acc;
        }, []);
    }, [type, categories]);
};
